import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import type { SceneNode } from "../scene/model";
import { selectable } from "../native/geometry";
import {
  duplicateLayers,
  moveLayersBefore,
  orderLayers,
  renameLayer,
  setLayerFlag,
  type LayerOrder,
} from "../native/layer-commands";
import { NativeLayerDrag } from "./native-layer-drag";
import { NativeLayerNames } from "./native-layer-names";
import { setNativeIcon } from "./native-icons";

/** Keyed layer list and its commands. Names and drag previews have separate lifecycles. */
export class NativeLayers {
  preserveAreaTool: () => boolean = () => false;
  private list = document.getElementById("native-layers")!;
  private rows = new Map<string, HTMLElement>();
  private names: NativeLayerNames;
  private drag: NativeLayerDrag;
  private dragRevision = -1;
  private dragGeneration = -1;
  private dragSelection = "";
  private lastTap: { id: string; time: number } | null = null;
  private lastNodes: readonly SceneNode[] | null = null;
  private lastSelection = "";
  private lastBusy = false;
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    private changed: () => void,
    private message: (text: string, error?: boolean) => void,
  ) {
    this.names = new NativeLayerNames(
      this.list,
      () => session.generation,
      (id, name) =>
        this.command(() => {
          const node = session.page.nodes.find((n) => n.id === id);
          if (node) session.replaceNode(renameLayer(node, name));
        }),
    );
    this.drag = new NativeLayerDrag(
      this.list,
      this.list.parentElement!,
      (id, group) => {
        if (pointer.active) return null;
        const members = session.page.nodes.filter((n) =>
          group ? n.layer.groupId === id : n.id === id,
        );
        if (!members.length || members.some((n) => n.layer.locked)) return null;
        this.names.finish(true);
        this.dragRevision = session.revision;
        this.dragGeneration = session.generation;
        this.dragSelection = JSON.stringify(session.selectedIds);
        const eligible = members.filter(selectable);
        const alreadySelected =
          eligible.length > 0 &&
          eligible.every((n) => session.selectedIds.includes(n.id));
        return [
          ...new Set([
            ...(alreadySelected ? session.selectedIds : []),
            ...members.map((n) => n.id),
          ]),
        ];
      },
      (ids, before) => {
        if (
          session.revision !== this.dragRevision ||
          session.generation !== this.dragGeneration ||
          JSON.stringify(session.selectedIds) !== this.dragSelection
        )
          return;
        this.command(() => {
          const nodes = moveLayersBefore(session.page.nodes, ids, before);
          if (nodes.every((n, i) => n === session.page.nodes[i])) return;
          session.replaceNodes(nodes);
          session.select(ids);
          pointer.setTool("select");
        });
      },
      changed,
    );
    this.list.addEventListener("click", (event) => {
      if (pointer.active || this.drag.active) return;
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "button",
      );
      const row = button?.closest<HTMLElement>("[data-layer-id]");
      const node = session.page.nodes.find(
        (n) => n.id === row?.dataset.layerId,
      );
      if (!button || !node || button.disabled) return;
      const action = button.dataset.layerAction;
      if (action === "visible" || action === "locked") {
        this.command(() => {
          const changed = setLayerFlag(
            node,
            action,
            action === "visible"
              ? node.appearance.visible === false
              : !node.layer.locked,
          );
          session.replaceNode(changed);
          if (selectable(changed)) session.selectedId = node.id;
        });
      } else if (button.dataset.nodeId) {
        if (!this.preserveAreaTool()) pointer.setTool("select");
        if (selectable(node))
          session.selectLayer(
            node.id,
            event.ctrlKey || event.metaKey,
            event.shiftKey,
          );
        else session.select([]);
        const touch = (event as PointerEvent).pointerType === "touch";
        const time = performance.now();
        if (
          touch &&
          this.lastTap?.id === node.id &&
          time - this.lastTap.time < 900
        ) {
          this.startRename(node.id);
          this.lastTap = null;
        } else this.lastTap = touch ? { id: node.id, time } : null;
        changed();
      }
    });
    this.list.addEventListener("dblclick", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>(
        "[data-node-id]",
      );
      if (button) this.startRename(button.dataset.nodeId!);
    });
    for (const button of document.querySelectorAll<HTMLButtonElement>(
      "[data-native-order]",
    ))
      button.onclick = () =>
        this.command(() =>
          session.replaceNodes(
            orderLayers(
              session.page.nodes,
              session.selectedIds,
              button.dataset.nativeOrder as LayerOrder,
            ),
          ),
        );
    for (const button of document.querySelectorAll<HTMLButtonElement>(
      "#native-duplicate, [data-native-duplicate]",
    ))
      button.onclick = () => this.duplicate();
  }
  get dragging() {
    return this.drag.active;
  }
  get interacting() {
    return this.drag.active || this.names.active;
  }
  cancel() {
    this.drag.cancel();
    this.names.finish(false);
  }
  private command(action: () => void) {
    if (this.pointer.active || this.drag?.active) return;
    try {
      action();
      this.session.finish();
    } catch (error) {
      this.message((error as Error).message, true);
    }
    this.changed();
  }
  private duplicate() {
    this.command(() => {
      const result = duplicateLayers(
        this.session.page.nodes,
        this.session.selectedIds,
        () => crypto.randomUUID(),
      );
      if (!result.ids.length) return;
      this.session.replaceNodes(result.nodes);
      this.session.select(result.ids);
      this.pointer.setTool("select");
    });
  }
  private startRename(id: string) {
    if (this.pointer.active || this.drag.active) return;
    const node = this.session.page.nodes.find((n) => n.id === id);
    if (node) this.names.start(id, String(node.layer.name ?? "Слой"));
  }
  keydown(event: KeyboardEvent) {
    if (this.drag.active) {
      if (!["Shift", "Alt", "Control", "Meta"].includes(event.key))
        this.drag.cancel();
      event.preventDefault();
      return true;
    }
    if (event.key !== "F2" || event.ctrlKey || event.metaKey || event.altKey)
      return false;
    const id =
      (event.target as HTMLElement).closest<HTMLElement>("[data-node-id]")
        ?.dataset.nodeId ?? this.session.selectedId;
    if (id) {
      event.preventDefault();
      this.startRename(id);
      return true;
    }
    return false;
  }
  private createRow(id: string) {
    const row = document.createElement("div");
    row.className = "native-layer-row";
    row.dataset.layerId = id;
    const button = (action: string, icon: string, title: string) => {
      const element = document.createElement("button");
      element.type = "button";
      element.dataset.layerAction = action;
      element.title = title;
      element.setAttribute("aria-label", title);
      setNativeIcon(element, icon);
      return element;
    };
    const name = document.createElement("button");
    name.type = "button";
    name.className = "native-layer-name";
    name.dataset.nodeId = id;
    name.append(document.createElement("span"));
    row.append(
      name,
      button("visible", "visible", "Скрыть слой"),
      button("locked", "unlocked", "Заблокировать слой"),
    );
    this.rows.set(id, row);
    return row;
  }
  render() {
    const session = this.session;
    if (
      this.drag.active &&
      (session.revision !== this.dragRevision ||
        session.generation !== this.dragGeneration ||
        JSON.stringify(session.selectedIds) !== this.dragSelection)
    )
      this.drag.cancel();
    const selection = JSON.stringify(session.selectedIds);
    const busy = this.pointer.active || this.drag.active;
    if (
      this.lastNodes === session.page.nodes &&
      this.lastBusy === busy &&
      this.lastSelection !== selection
    ) {
      const changed = new Set<string>([
        ...JSON.parse(this.lastSelection || "[]"),
        ...session.selectedIds,
      ]);
      for (const id of changed) {
        const row = this.rows.get(id),
          selected = session.selectedIds.includes(id);
        if (row) {
          row.dataset.selected = String(selected);
          row
            .querySelector("[data-node-id]")!
            .setAttribute("aria-pressed", String(selected));
        }
      }
      this.lastSelection = selection;
      (
        document.getElementById("native-duplicate") as HTMLButtonElement
      ).disabled = busy || !session.selectedNodes.length;
      return;
    }
    if (
      this.lastNodes === session.page.nodes &&
      this.lastSelection === selection &&
      this.lastBusy === busy
    )
      return;
    const nodesChanged = this.lastNodes !== session.page.nodes;
    this.lastNodes = session.page.nodes;
    this.lastSelection = selection;
    this.lastBusy = busy;
    const ids = session.page.nodes.map((n) => n.id);
    const present = new Set(ids),
      selectedIds = new Set(session.selectedIds);
    this.names.reconcile(ids);
    for (const [id, row] of this.rows)
      if (!present.has(id)) {
        row.remove();
        this.rows.delete(id);
      }
    session.page.nodes
      .slice()
      .reverse()
      .forEach((node, index) => {
        const row = this.rows.get(node.id) ?? this.createRow(node.id);
        const name = row.querySelector<HTMLButtonElement>("[data-node-id]")!;
        const selected = selectedIds.has(node.id);
        row.dataset.selected = String(selected);
        row.dataset.hidden = String(node.appearance.visible === false);
        row.dataset.locked = String(node.layer.locked === true);
        name.disabled = this.pointer.active;
        name.setAttribute("aria-pressed", String(selected));
        const text = String(node.layer.name ?? "Слой");
        name.title = text + " · Двойной щелчок или F2 — переименовать";
        const label = name.querySelector("span")!;
        if (label.textContent !== text) label.textContent = text;
        name.style.setProperty(
          "--layer-color",
          String(node.appearance.fill ?? "#94a3b8"),
        );
        for (const action of ["visible", "locked"] as const) {
          const control = row.querySelector<HTMLButtonElement>(
            `[data-layer-action=${action}]`,
          )!;
          const pressed =
            action === "visible"
              ? node.appearance.visible !== false
              : node.layer.locked === true;
          const title =
            action === "visible"
              ? pressed
                ? "Скрыть слой"
                : "Показать слой"
              : pressed
                ? "Разблокировать слой"
                : "Заблокировать слой";
          control.title = title;
          control.setAttribute("aria-label", title + ": " + text);
          control.setAttribute("aria-pressed", String(pressed));
          control.disabled = this.pointer.active || this.drag.active;
          setNativeIcon(
            control,
            action === "visible"
              ? pressed
                ? "visible"
                : "hidden"
              : pressed
                ? "locked"
                : "unlocked",
          );
        }
        if (nodesChanged && this.list.children[index] !== row)
          this.list.insertBefore(row, this.list.children[index] ?? null);
      });
    for (const button of document.querySelectorAll<HTMLButtonElement>(
      "[data-native-order]",
    )) {
      const ordered = orderLayers(
        session.page.nodes,
        session.selectedIds,
        button.dataset.nativeOrder as LayerOrder,
      );
      button.disabled =
        busy || ordered.every((n, i) => n === session.page.nodes[i]);
    }
    (
      document.getElementById("native-duplicate") as HTMLButtonElement
    ).disabled = busy || !session.selectedNodes.length;
    document.getElementById("native-no-layers")!.hidden = ids.length > 0;
  }
}
